import { useState, useEffect, useCallback } from 'react';
import {
  Card, BlockStack, InlineStack, InlineGrid, Text, Button, Badge, Select, Checkbox, Spinner,
} from '@shopify/polaris';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Cell,
} from 'recharts';
import { apiGet } from '../api';
import KPICard from './KPICard';

const COLORS = {
  matcha: '#7E942B',
  gold: '#D89C70',
  darkGreen: '#5A6B1F',
  orange: '#E8A87C',
  grey: '#8C8C8C',
  blue: '#6B9BC3',
};

const FUNNEL_COLORS = [COLORS.matcha, COLORS.gold, COLORS.darkGreen, COLORS.orange, COLORS.blue];

interface FunnelData {
  homeViews: number;
  productViews: number;
  addToCarts: number;
  beginCheckouts: number;
  purchases: number;
  totalSessions: number;
  uniqueUsers: number;
}

interface DailyData {
  day: string;
  homeViews: number;
  productViews: number;
  addToCarts: number;
  beginCheckouts: number;
  purchases: number;
}

interface AvgTime { screen: string; avgSeconds: number; }
interface TopProduct { handle: string; title: string; views: number; }

interface FunnelResponse {
  funnel: FunnelData;
  comparison: FunnelData | null;
  daily: DailyData[];
  avgTimePerScreen: AvgTime[];
  topProducts: TopProduct[];
}

function pctChange(curr: number, prev: number): number | null {
  if (prev === 0) return curr > 0 ? 100 : null;
  return ((curr - prev) / prev) * 100;
}

function convRate(from: number, to: number): string {
  if (from === 0) return '0%';
  return `${((to / from) * 100).toFixed(1)}%`;
}

type RangeKey = '7d' | '30d' | '90d' | 'custom';

export default function FunnelChart() {
  const [range, setRange] = useState<RangeKey>('30d');
  const [compare, setCompare] = useState(false);
  const [data, setData] = useState<FunnelResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const days = range === '7d' ? 7 : range === '90d' ? 90 : 30;
    const to = new Date();
    const from = new Date(Date.now() - days * 86400000);

    let url = `/panel-api/analytics/funnel?from=${from.toISOString()}&to=${to.toISOString()}`;
    if (compare) {
      const compareTo = new Date(from.getTime());
      const compareFrom = new Date(from.getTime() - days * 86400000);
      url += `&compareFrom=${compareFrom.toISOString()}&compareTo=${compareTo.toISOString()}`;
    }

    try {
      const result = await apiGet<FunnelResponse>(url);
      setData(result);
    } catch (err) {
      console.error('Funnel fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [range, compare]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return <Card><BlockStack align="center" inlineAlign="center"><Spinner size="large" /></BlockStack></Card>;
  }

  const { funnel, comparison, daily, avgTimePerScreen, topProducts } = data;
  const overallConv = funnel.homeViews > 0 ? ((funnel.purchases / funnel.homeViews) * 100).toFixed(2) : '0';

  const funnelSteps = [
    { label: 'Home Views', key: 'homeViews' as const },
    { label: 'Product Views', key: 'productViews' as const },
    { label: 'Add to Cart', key: 'addToCarts' as const },
    { label: 'Begin Checkout', key: 'beginCheckouts' as const },
    { label: 'Purchase', key: 'purchases' as const },
  ];

  const funnelBarData = funnelSteps.map((s, i) => ({
    name: s.label,
    current: funnel[s.key],
    previous: comparison ? comparison[s.key] : 0,
    fill: FUNNEL_COLORS[i],
  }));

  return (
    <BlockStack gap="400">
      {/* Controls */}
      <Card>
        <InlineStack gap="400" align="start" blockAlign="center">
          <Select
            label="Period"
            labelInline
            options={[
              { label: '7 days', value: '7d' },
              { label: '30 days', value: '30d' },
              { label: '90 days', value: '90d' },
            ]}
            value={range}
            onChange={(v) => setRange(v as RangeKey)}
          />
          <Checkbox label="Compare previous period" checked={compare} onChange={setCompare} />
          <Button onClick={fetchData}>Refresh</Button>
          <Badge tone="info">{`Overall: ${overallConv}% conversion`}</Badge>
        </InlineStack>
      </Card>

      {/* KPI Cards */}
      <InlineGrid columns={{ xs: 1, sm: 2, md: 5 }} gap="400">
        {funnelSteps.map((s) => (
          <KPICard
            key={s.key}
            title={s.label}
            value={funnel[s.key]}
            change={comparison ? pctChange(funnel[s.key], comparison[s.key]) : undefined}
          />
        ))}
      </InlineGrid>

      {/* Conversion rates between steps */}
      <Card>
        <BlockStack gap="200">
          <Text as="h2" variant="headingMd">Step Conversion Rates</Text>
          <InlineStack gap="400">
            {funnelSteps.slice(0, -1).map((s, i) => {
              const next = funnelSteps[i + 1];
              return (
                <Badge key={s.key} tone="success">
                  {`${s.label} → ${next.label}: ${convRate(funnel[s.key], funnel[next.key])}`}
                </Badge>
              );
            })}
          </InlineStack>
        </BlockStack>
      </Card>

      {/* Funnel bar chart */}
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">Funnel</Text>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={funnelBarData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="current" name="Current" radius={[4, 4, 0, 0]}>
                {funnelBarData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
              {comparison && (
                <Bar dataKey="previous" name="Previous" fill={COLORS.grey} radius={[4, 4, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </BlockStack>
      </Card>

      {/* Daily trend */}
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">Daily Trends</Text>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="homeViews" stroke={COLORS.matcha} name="Home" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="productViews" stroke={COLORS.gold} name="Product" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="addToCarts" stroke={COLORS.darkGreen} name="Add to Cart" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="beginCheckouts" stroke={COLORS.orange} name="Checkout" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="purchases" stroke={COLORS.blue} name="Purchase" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </BlockStack>
      </Card>

      {/* Avg time per screen & top products */}
      <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Avg Time per Screen</Text>
            {avgTimePerScreen.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={avgTimePerScreen} layout="vertical" margin={{ left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" unit="s" />
                  <YAxis type="category" dataKey="screen" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip formatter={(v: number | undefined) => `${v ?? 0}s`} />
                  <Bar dataKey="avgSeconds" fill={COLORS.matcha} name="Avg seconds" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Text as="p" tone="subdued">No screen time data yet</Text>
            )}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Top Products Viewed</Text>
            {topProducts.length > 0 ? (
              <BlockStack gap="200">
                {topProducts.map((p, i) => (
                  <InlineStack key={p.handle} gap="200" align="space-between" blockAlign="center">
                    <Text as="span" variant="bodyMd">
                      {i + 1}. {p.title || p.handle}
                    </Text>
                    <Badge>{`${String(p.views)} views`}</Badge>
                  </InlineStack>
                ))}
              </BlockStack>
            ) : (
              <Text as="p" tone="subdued">No product view data yet</Text>
            )}
          </BlockStack>
        </Card>
      </InlineGrid>
    </BlockStack>
  );
}
